import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { getDateLocale } from "@/utils/dateUtils";
import { format, addDays, startOfWeek } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useSettingsStore } from "@/store/api/settingsStore";
import { DEFAULT_OPEN_DAYS_OF_WEEK } from "@/utils/scheduleUtils";

interface CalendarViewProps {
  selectedDate: Date;
  currentMonth: Date;
  onDateSelect: (date: Date) => void;
  onMonthChange: (month: Date) => void;
  compact?: boolean;
}

export function CalendarView({
  selectedDate,
  currentMonth,
  onDateSelect,
  onMonthChange,
  compact = false,
}: CalendarViewProps) {
  const { t } = useTranslation('schedule');
  const locale = getDateLocale();
  const [isOpen, setIsOpen] = useState(false);

  const openDaysOfWeek = useSettingsStore(
    (s) => s.settings.schedule?.openDaysOfWeek ?? DEFAULT_OPEN_DAYS_OF_WEEK
  );

  // Get current week days, filtered by openDaysOfWeek
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const allowed = openDaysOfWeek.length > 0 ? openDaysOfWeek : DEFAULT_OPEN_DAYS_OF_WEEK;
  const weekDays = Array.from({ length: 7 })
    .map((_, i) => addDays(weekStart, i))
    .filter((d) => allowed.includes(d.getDay()));

  return (
    <Card className={cn("schedule-calendar w-full h-fit", compact && "schedule-calendar--compact")}>
      <CardHeader className={cn("flex flex-row items-center justify-between", compact ? "px-3 py-1.5" : "py-3")}>
        <CardTitle className={cn(compact ? "text-xs" : "text-sm")}>{t('calendar')}</CardTitle>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className={cn(compact && "h-7 w-7 p-0")}>
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                onDateSelect(date);
                setIsOpen(false);
              }}
              onMonthChange={onMonthChange}
              month={currentMonth}
              locale={locale}
              className="rounded-md bg-background"
              classNames={{
                day_today: "bg-primary text-primary-foreground",
                day_selected: "bg-secondary text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground",
                months: "space-y-2",
                month: "space-y-2",
                caption: "flex justify-center pt-1 relative items-center text-xs",
                caption_label: "text-xs font-medium",
                nav: "space-x-1 flex items-center",
                nav_button: "h-6 w-6 bg-transparent p-0 opacity-50 hover:opacity-100",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-muted-foreground rounded-md w-7 font-normal text-[0.7rem]",
                row: "flex w-full mt-1",
                cell: "h-7 w-7 text-center text-xs p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-7 w-7 p-0 font-normal aria-selected:opacity-100 text-xs bg-background",
              }}
            />
          </PopoverContent>
        </Popover>
      </CardHeader>
      <CardContent className={cn("p-0", compact ? "pb-1" : "pb-2")}>
        <div className={cn("flex justify-between items-center", compact ? "px-1.5" : "px-2")}>
          {weekDays.map((day, index) => (
            <Button 
              key={index}
              variant="ghost" 
              size="sm"
              className={cn(
                "flex flex-col items-center justify-center rounded-md text-xs",
                compact ? "h-10 min-w-0 flex-1 p-0.5" : "h-14 w-9 p-1",
                format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') 
                  ? "bg-secondary text-secondary-foreground" 
                  : "hover:bg-secondary/50"
              )}
              onClick={() => onDateSelect(day)}
            >
              <span className={cn("font-medium", compact ? "text-[9px] leading-none" : "text-[0.65rem]")}>{format(day, 'EEE', { locale })}</span>
              <span className={cn("font-semibold", compact ? "mt-0.5 text-sm leading-none" : "text-lg")}>{format(day, 'd')}</span>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
